import React, { useState, useEffect, useCallback } from 'react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ManualPlay from './components/ManualPlay';
import GoalStatus from './components/GoalStatus';
import LoginLogs from './components/LoginLogs';
import MyCoupons from './components/MyCoupons';
import ProxyTest from './components/ProxyTest';
import ProxyNodeTest from './components/ProxyNodeTest';
import AccountActivities from './components/AccountActivities';
import BetLogs from './components/BetLogs';
import ManualBetLogs from './components/ManualBetLogs';
import EsporResults from './components/EsporResults';
import CouponAnalysis from './components/CouponAnalysis';
import NesineAccounts from './components/NesineAccounts';
import ServerErrors from './components/ServerErrors';
import './App.css';

const API_BASE = '/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [activePage, setActivePage] = useState('goals');
  const [esporInitialView, setEsporInitialView] = useState(null);

  const navigateToEspor = (view) => {
    setEsporInitialView(view);
    setActivePage('esporResults');
  };

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.status === 401) {
        localStorage.removeItem('token');
      } else if (!res.ok) {
        throw new Error(data.message || 'Oturum doğrulanamadı.');
      } else if (data.success) {
        setIsAuthenticated(true);
        setUser(data.user);
      } else {
        throw new Error(data.message || 'Oturum doğrulanamadı.');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthError('Oturum doğrulanamadı. Sunucu bağlantısını kontrol edip yeniden deneyin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = (token, user) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    setUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner" />
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app">
        <Login onLogin={handleLogin} initialError={authError} />
      </div>
    );
  }

  const menuItems = [
    { id: 'goals', label: 'Otokupon Monitör', icon: '⚽' },
    { id: 'manual', label: 'Espor Monitör', icon: '🎮' },
    { id: 'esporResults', label: 'Espor Sonuçları', icon: '📊' },
    { id: 'betLogs', label: 'Otokupon Logs', icon: '🎫' },
    { id: 'manualLogs', label: 'Manuel Kupon Logs', icon: '📋' },
    { id: 'accountActivities', label: 'Hesap Hareketleri', icon: '💰' },
    { id: 'myCoupons', label: 'Kuponlarım', icon: '🎫' },
    { id: 'couponAnalysis', label: 'Kupon Analizi', icon: '📊' },
    { id: 'logs', label: 'Nesine Login Logs', icon: '📋' },
    { id: 'proxyTest', label: 'Proxy Test', icon: '🌐' },
    { id: 'proxyNodeTest', label: 'Node Proxy Test', icon: '🔌' },
    { id: 'serverErrors', label: 'Server Hataları', icon: '⚠️' },
    { id: 'nesineAccounts', label: 'Kullanıcılar', icon: '👥' },
    { id: 'nesine', label: 'Ayarlar', icon: '⚙️' },
  ];

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>🤖 Nesine Bot</h1>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activePage === item.id ? 'nav-active' : ''}`}
              onClick={() => setActivePage(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <span className="sidebar-user-icon">👤</span>
            <span className="sidebar-user-name">{user.username}</span>
          </div>
          <button onClick={handleLogout} className="sidebar-logout">
            Çıkış
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {activePage === 'nesine' && (
          <Dashboard user={user} onLogout={handleLogout} />
        )}
        {activePage === 'manual' && (
          <ManualPlay onNavigate={navigateToEspor} />
        )}
        {activePage === 'goals' && (
          <GoalStatus />
        )}
        {activePage === 'esporResults' && (
          <EsporResults initialView={esporInitialView} onViewConsumed={() => setEsporInitialView(null)} />
        )}
        {activePage === 'betLogs' && (
          <BetLogs />
        )}
        {activePage === 'manualLogs' && (
          <ManualBetLogs />
        )}
        {activePage === 'logs' && (
          <LoginLogs />
        )}
        {activePage === 'myCoupons' && (
          <MyCoupons onNavigate={(page) => setActivePage(page)} />
        )}
        {activePage === 'couponAnalysis' && (
          <CouponAnalysis />
        )}
        {activePage === 'proxyTest' && (
          <ProxyTest />
        )}
        {activePage === 'proxyNodeTest' && (
          <ProxyNodeTest />
        )}
        {activePage === 'nesineAccounts' && (
          <NesineAccounts />
        )}
        {activePage === 'serverErrors' && (
          <ServerErrors />
        )}
        {activePage === 'accountActivities' && (
          <AccountActivities />
        )}
      </main>
    </div>
  );
}

export default App;
